import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, EMPTY, Observable, Subscription, timer } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  Base64File,
  KybEntityKeyCompanyDetails,
  KybEntityIndividualPartySummary,
  KybEntitySummary,
  KybManualPerson,
  KybPerson,
  KybPersonRowState,
  KybReconciliationAction,
  KybReconciliationActionInput,
  KybReconciliationApplicationPerson,
  KybReconciliationSaveData,
  KybReconciliationSaveResult,
  KybValidationResult,
  PlacesDetailsFn,
  PlacesSearchResultFn,
  RemoveApplicationPartyInput,
  SaveApplicationPartyEntityInput,
  SavePartyChangedField,
  TrustAnalysis,
  filesToBase64Files,
  kybEntityAmlScreeningCheck,
  kybEntityAsKeyPeopleResult,
  kybEntityBlocklistCheck,
  kybEntityKeyPeopleValidation,
  kybEntityIndividualPartyName,
  kybEntityIndividualPartyRoleLabel,
  kybEntityNaturalPersonParties,
  kybEntityRiskLevelClass,
  kybEntityRiskLevelLabel,
  kybEntityRiskScoreLabel,
  kybEntityRoleLabel,
  kybEntityStatusDisplay,
  kybEntityTypeLabel,
  kybResolveSelected,
  compareIdentity,
  personIdentityPartialMatch,
  trustAnalysisDisplayStatus,
  trustEntityDeedValue,
} from '@portal-workspace/grow-shared-library';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { openWindowAndDownloadWithFilename, setupUntilDestroy } from '../component-utils';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { KybStatusChipComponent } from './kyb-status-chip.component';
import { KybRiskBadgeComponent } from './kyb-risk-badge.component';
import { KybKeyCompanyDetailsForm, KybResultBlockComponent } from './kyb-result-block.component';
import { TrustAnalysisBlockComponent } from './trust-analysis-block.component';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { KybPersonDetailsRequired, KybPersonResolution } from '../kyb-key-people-component/kyb-entity-section.component';
import { KybCompanyDetailsValue, kybBusinessEmailValidator, kybCompanyDetailsChanged, kybCompanyDetailsChangedEntries, kybCompanyDetailsEmailChanged } from './kyb-company-details';
import { ApplicationService } from '../../../../../../apps/portal2/src/app/service/application.service';
import { Buffer } from 'buffer';

/** A party related to the entity: a corporate trustee/partner child, or an individual who signs for it. */
export interface KybEntitySubLine {
  name: string;
  role: string;
}

/**
 * One row per top-level entity. Corporate trustees/partners and the individuals who sign for the
 * entity are summarised as a single caption under its name rather than as rows of their own; a
 * corporate trustee still has its own KYB run, which is shown as its own result block inside the
 * expansion.
 */
export interface KybEntityRow {
  entity: KybEntitySummary;
  /** One "Role: Name" entry per related party ("Trustee: ACME PTY LTD", "Signer: Jane Citizen"). */
  parties: string[];
}

/** Every analysis of one deed condition, newest first, so versions[0] is the deed as it now stands. */
export interface TrustAnalysisHistory {
  key: string;
  versions: TrustAnalysis[];
}

export interface KybApplicationReconciliationReview {
  entity: KybEntitySummary;
  person: KybReconciliationApplicationPerson;
  decision: Extract<KybReconciliationAction, 'KEEP' | 'REMOVE'> | null;
  error: string | null;
}

export interface KybReconciliationDraftRestore {
  data: KybReconciliationSaveData;
  error: string | null;
}

/** Long enough not to hammer the API, short enough that a reviewer waiting on a deed sees it land. */
const TRUST_ANALYSIS_POLL_INTERVAL_MS = 15000;

export const failedKybReconciliationDrafts = (
  submitted: KybReconciliationSaveData[],
  results: KybReconciliationSaveResult[],
): KybReconciliationDraftRestore[] => {
  const failureByGuid = new Map(
    results.filter(result => !result.saved).map(result => [result.reconciliationGuid, result.message]),
  );
  return submitted
    .filter(draft => failureByGuid.has(draft.reconciliationGuid))
    .map(data => ({
      data,
      error: failureByGuid.get(data.reconciliationGuid) ?? 'Unable to complete reconciliation.',
    }));
};

const buildSubLines = (entity: KybEntitySummary): KybEntitySubLine[] =>
  [
    ...entity.children.map(child => ({ name: child.entityName, role: kybEntityRoleLabel(child) })),
    ...entity.individualParties
      .filter(party => party.isSigner)
      .map(party => ({
        name: kybEntityIndividualPartyName(party),
        role: kybEntityIndividualPartyRoleLabel(party),
      })),
  ].filter(line => !!line.name);

const toEntityRows = (entities: KybEntitySummary[]): KybEntityRow[] =>
  entities.map(entity => ({
    entity,
    parties: buildSubLines(entity).map(line => `${line.role}: ${line.name}`),
  }));

export class KybEntitySummaryDataSource extends DataSource<KybEntityRow> {
  subject = new BehaviorSubject<KybEntityRow[]>([]);

  connect(collectionViewer: CollectionViewer): Observable<KybEntityRow[]> {
    return this.subject.asObservable();
  }

  disconnect(_collectionViewer: CollectionViewer): void {
    // no-op: this source outlives the table, which is destroyed while the loading spinner shows
  }

  update(data: KybEntityRow[]) {
    this.subject.next(data);
  }
}

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'kyb-entity-summary',
  templateUrl: './kyb-entity-summary.component.html',
  styleUrls: ['./kyb-entity-summary.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*', display: 'block' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: true,
  imports: [
    FlexModule,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatTooltipModule,
    DatePipe,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    ExtendedModule,
    MessageBoxComponent,
    KybStatusChipComponent,
    KybRiskBadgeComponent,
    KybResultBlockComponent,
    TrustAnalysisBlockComponent,
  ],
})
export class KybEntitySummaryComponent implements OnInit {
  @Input({ required: true }) applicationId!: number;
  @Input() canEdit = false;
  @Input() canRunWorkflow = false;
  /** Internal staff may send a FrankieOne risk re-evaluation. The API enforces the same set. */
  @Input() canReevaluateRisk = false;
  /** True once documents have been issued; add/remove of the party structure is then frozen. */
  @Input() partyStructureLocked = false;
  @Input() placesSearchResultFn: PlacesSearchResultFn | null = null;
  @Input() placesDetailsFn: PlacesDetailsFn | null = null;
  /** A tick on a stakeholder with no application individual yet; the KYC/AML tab captures their details. */
  @Output() keyPersonDetailsRequired: EventEmitter<KybPersonDetailsRequired> = new EventEmitter<KybPersonDetailsRequired>();
  @Output() personResolutionUndo: EventEmitter<KybPerson> = new EventEmitter<KybPerson>();
  /** Add a corporate trustee/partner under this entity. Guarantors are added from the tab header. */
  @Output() addParty: EventEmitter<KybEntitySummary> = new EventEmitter<KybEntitySummary>();
  /** Add a natural-person trustee/partner under this entity. */
  @Output() addIndividualParty: EventEmitter<KybEntitySummary> = new EventEmitter<KybEntitySummary>();
  /** Remove this non-primary entity; staged by the parent and applied on the tab-level Save. */
  @Output() removeParty: EventEmitter<KybEntitySummary> = new EventEmitter<KybEntitySummary>();
  /** Undo a staged entity removal before Save. */
  @Output() undoRemoveParty: EventEmitter<KybEntitySummary> = new EventEmitter<KybEntitySummary>();
  /** Remove an individual trustee/partner; staged by the parent and applied on the tab-level Save. */
  @Output() removeIndividualParty: EventEmitter<KybEntityIndividualPartySummary> = new EventEmitter<KybEntityIndividualPartySummary>();

  dataSource = new KybEntitySummaryDataSource();
  columnsToDisplay = ['dir', 'name', 'entityType', 'entityRole', 'riskScore', 'riskRating', 'kybStatus', 'action'];
  expandedEntityId: number | null = null;
  rows: KybEntityRow[] = [];
  loaded = false;
  subscriptions: Subscription[] = [];

  /** Keyed by commercialEntityId; only the primary borrowing entity has a form. */
  entityForms = new Map<number, FormGroup<KybKeyCompanyDetailsForm>>();

  /**
   * Trust deed analyses per entity, keyed by commercialEntityId, newest first.
   *
   * A list rather than a single analysis: a condition can hold more than one deed, and an entity
   * can be re-analysed, so showing only the latest hid results a reviewer needs.
   */
  trustAnalyses = new Map<number, TrustAnalysis[]>();
  /**
   * Those same analyses grouped into one history per deed condition, keyed by commercialEntityId.
   *
   * Built when the analyses load rather than derived in the template: the grouping allocates, and a
   * template getter would rebuild it on every change-detection cycle and hand the blocks a new
   * versions array each time.
   */
  trustAnalysisHistories = new Map<number, TrustAnalysisHistory[]>();
  /** The version the reviewer has switched to, keyed by TrustAnalysisHistory.key. */
  private selectedTrustRunIds = new Map<string, number>();
  /** Pending analyses are polled so a result appears without the reviewer reloading the tab. */
  private trustAnalysisPollSubscription: Subscription | null = null;

  // Editing is opt-in per entity: these edits void and reissue privacy consent, so the panel opens
  // read-only and the reviewer has to ask for the fields before they can change anything.
  private editingEntityIds = new Set<number>();
  /** Values as loaded, so cancelling an edit puts the fields back and a save can tell what changed. */
  private pristineEntityValues = new Map<number, KybCompanyDetailsValue>();
  /** Guarantor/signer/select ticks the reviewer has changed, keyed by stakeholderId. */
  private keyPersonChanges = new Map<number, KybPersonRowState>();
  /** Entities staged for removal, keyed by commercialEntityId; applied on the tab-level Save. */
  private stagedEntityRemovals = new Map<number, KybEntitySummary>();
  /** Individual trustees/partners staged for removal, keyed by individualId; applied on Save. */
  private stagedIndividualRemovals = new Map<number, KybEntityIndividualPartySummary>();
  /**
   * Signer/guarantor rule messages per entity, keyed by commercialEntityId (nested corporate
   * trustees/partners included, since each has its own people table).
   *
   * Held here rather than computed by the result block so the messages are recomputed on every tick
   * and survive the row being collapsed, which destroys that block along with its table.
   */
  private keyPeopleValidation = new Map<number, KybValidationResult>();
  private keyPeopleRequirementIssueList: Array<{
    entityId: number;
    entityName: string;
    errors: string[];
  }> = [];
  private reconciliationChanges = new Map<number, KybReconciliationSaveData>();
  private reconciliationLinks = new Map<number, Map<number, number>>();
  // By stakeholder id. A link outside a people review has no application person to take a name from.
  private reconciliationLinkNames = new Map<number, string>();
  private reconciliationAdditions = new Map<number, Map<number, KybManualPerson>>();
  private applicationReconciliationDecisions =
    new Map<number, Map<number, Extract<KybReconciliationAction, 'KEEP' | 'REMOVE'>>>();
  private reconciliationReviewNotes = new Map<number, string>();
  private touchedReconciliationEntityIds = new Set<number>();
  reconciliationValidation = new Map<number, string>();
  private workflowStartingEntityIds = new Set<number>();
  private reevaluationStartingEntityIds = new Set<number>();
  readonly reevaluateRiskTooltip =
    'Re-score this entity in FrankieOne, patching the ANZSIC, channel and product first where the entity has an ANZSIC. Does not start a new KYB workflow.';

  typeLabel = kybEntityTypeLabel;
  roleLabel = kybEntityRoleLabel;
  statusDisplay = kybEntityStatusDisplay;
  riskScoreLabel = kybEntityRiskScoreLabel;
  riskLevelLabel = kybEntityRiskLevelLabel;
  riskLevelClass = kybEntityRiskLevelClass;
  naturalPersonParties = kybEntityNaturalPersonParties;
  individualPartyName = kybEntityIndividualPartyName;
  individualPartyRoleLabel = kybEntityIndividualPartyRoleLabel;

  constructor(
    private formBuilder: FormBuilder,
    private applicationService: ApplicationService,
    private applicationDialogService: ApplicationDialogService,
    private portalHotToastService: PortalHotToastService,
  ) {}

  ngOnInit() {
    setupUntilDestroy(this);
    this.refresh();
  }

  /**
   * @param onLoaded Runs once the reloaded entities are on screen, for a caller with work that
   * depends on them - staging a tick against a stakeholder whose entity was created moments ago.
   */
  refresh(draftsToRestore: KybReconciliationDraftRestore[] = [], onLoaded?: () => void) {
    this.loaded = false;
    this.applicationService.kybEntitySummaryFn(this.applicationId).pipe(
      this.portalHotToastService.topMenuLoadingObservable(),
      tap(result => {
        this.rows = toEntityRows(result.entities);
        this.dataSource.update(this.rows);
        this.buildEntityForms(this.rows);
        this.restoreReconciliationDrafts(draftsToRestore);
        this.recomputeKeyPeopleValidation();
        if (this.expandedEntityId != null && !this.rows.some(row => row.entity.commercialEntityId === this.expandedEntityId)) {
          this.expandedEntityId = null;
        }
        onLoaded?.();
      }),
      finalize(() => {
        this.loaded = true;
      }),
    ).subscribe();
    this.refreshTrustAnalyses();
  }

  /**
   * Loaded alongside the entity summary rather than as part of it: most applications have no trust
   * deed, and a trust analysis failing to load must not take the Business Entities table with it.
   */
  private refreshTrustAnalyses() {
    this.applicationService.trustAnalysisSummaryFn(this.applicationId).pipe(
      tap(result => {
        this.trustAnalyses.clear();
        for (const analysis of result.analyses) {
          const forEntity = this.trustAnalyses.get(analysis.commercialEntityId) ?? [];
          forEntity.push(analysis);
          this.trustAnalyses.set(analysis.commercialEntityId, forEntity);
        }
        this.buildTrustAnalysisHistories();
        this.scheduleTrustAnalysisPoll(result.analyses);
      }),
    ).subscribe();
  }

  /**
   * Group each entity's analyses into one history per deed condition.
   *
   * Every deed in a condition is analysed, so a deed of variation uploaded after the original leaves
   * two analyses describing the same trust. They belong in one block the reviewer switches between
   * rather than two blocks side by side, where there would be nothing to say which structure is the
   * one that currently applies.
   *
   * Analyses arrive newest first, so each history's first version is its current one. Runs predating
   * per-entity conditions carry no condition value and are grouped together under the entity.
   */
  private buildTrustAnalysisHistories() {
    this.trustAnalysisHistories.clear();
    for (const [commercialEntityId, analyses] of this.trustAnalyses) {
      const byKey = new Map<string, TrustAnalysisHistory>();
      const histories: TrustAnalysisHistory[] = [];
      for (const analysis of analyses) {
        const key = `${commercialEntityId}:${analysis.conditionValue ?? 'legacy'}`;
        const existing = byKey.get(key);
        if (existing) {
          existing.versions.push(analysis);
        } else {
          const history: TrustAnalysisHistory = { key, versions: [analysis] };
          byKey.set(key, history);
          histories.push(history);
        }
      }
      this.trustAnalysisHistories.set(commercialEntityId, histories);
    }
  }

  /**
   * Re-read the analyses while any is still running, so a reviewer watching the tab sees the result
   * appear. Background polling with no user action, so it carries no toast.
   */
  private scheduleTrustAnalysisPoll(analyses: TrustAnalysis[]) {
    this.trustAnalysisPollSubscription?.unsubscribe();
    this.trustAnalysisPollSubscription = null;
    const anyPending = analyses.some(analysis => trustAnalysisDisplayStatus(analysis.status) === 'pending');
    if (!anyPending) {
      return;
    }
    this.trustAnalysisPollSubscription = timer(TRUST_ANALYSIS_POLL_INTERVAL_MS).pipe(
      tap(() => this.refreshTrustAnalyses()),
    ).subscribe();
    this.subscriptions.push(this.trustAnalysisPollSubscription);
  }

  private buildEntityForms(rows: KybEntityRow[]) {
    this.entityForms.clear();
    this.pristineEntityValues.clear();
    this.editingEntityIds.clear();
    this.keyPersonChanges.clear();
    this.stagedEntityRemovals.clear();
    this.stagedIndividualRemovals.clear();
    this.reconciliationChanges.clear();
    this.reconciliationLinks.clear();
    this.reconciliationLinkNames.clear();
    this.reconciliationAdditions.clear();
    this.applicationReconciliationDecisions.clear();
    this.reconciliationReviewNotes.clear();
    this.touchedReconciliationEntityIds.clear();
    this.reconciliationValidation.clear();
    for (const row of rows) {
      const details = row.entity.keyCompanyDetails;
      if (!details) {
        continue;
      }
      const group = this.formBuilder.group<KybKeyCompanyDetailsForm>({
        annualRevenue: this.formBuilder.control(details.annualRevenue),
        businessPhone: this.formBuilder.control(joinBusinessPhone(details)),
        businessContactEmail: this.formBuilder.control(details.businessContactEmail, [kybBusinessEmailValidator]),
        principalPlaceOfBusinessAddress: this.formBuilder.control(details.principalPlaceOfBusinessAddress),
      });
      group.disable();
      this.entityForms.set(row.entity.commercialEntityId, group);
      this.pristineEntityValues.set(row.entity.commercialEntityId, group.getRawValue());
    }
  }

  isEditing(row: KybEntityRow): boolean {
    return this.editingEntityIds.has(row.entity.commercialEntityId);
  }

  startEdit(row: KybEntityRow) {
    const entityId = row.entity.commercialEntityId;
    this.editingEntityIds.add(entityId);
    this.entityForms.get(entityId)?.enable();
    this.expandedEntityId = entityId;
  }

  /**
   * Discard this entity's edits.
   *
   * The row is collapsed as part of cancelling, which destroys the result block and with it the
   * key-people table's own copy of the ticks - that table edits the person objects it is handed, so
   * re-expanding is what puts the saved values back on screen.
   */
  cancelEdit(row: KybEntityRow) {
    const entityId = row.entity.commercialEntityId;
    this.editingEntityIds.delete(entityId);
    for (const stakeholderId of stakeholderIdsOf(row.entity)) {
      this.keyPersonChanges.delete(stakeholderId);
    }
    this.recomputeKeyPeopleValidation();
    if (this.expandedEntityId === entityId) {
      this.expandedEntityId = null;
    }
    const group = this.entityForms.get(entityId);
    if (!group) {
      return;
    }
    const pristine = this.pristineEntityValues.get(entityId);
    if (pristine) {
      group.reset(pristine);
    }
    // Re-locking also drops the entity from dirtyEntities, which skips disabled groups.
    group.disable();
  }

  onKeyPersonChange(state: KybPersonRowState) {
    this.keyPersonChanges.set(state.stakeholderId, state);
    const entity = this.reconciliationEntityForStakeholder(state.stakeholderId);
    const stagedPerson = entity
      ? this.reconciliationAdditions.get(entity.commercialEntityId)?.get(state.stakeholderId)
      : null;
    if (entity && stagedPerson) {
      this.reconciliationAdditions.get(entity.commercialEntityId)?.set(state.stakeholderId, {
        ...stagedPerson,
        isGuarantor: state.isGuarantor,
        isSigner: state.isSigner,
      });
      this.reconciliationChanges.delete(entity.commercialEntityId);
    }
    if (entity?.reconciliation?.status === 'REVIEW_REQUIRED') {
      this.reconciliationChanges.delete(entity.commercialEntityId);
      this.reconciliationValidation.delete(entity.commercialEntityId);
    }
    this.recomputeKeyPeopleValidation();
  }

  @ViewChildren(KybResultBlockComponent) private resultBlocks?: QueryList<KybResultBlockComponent>;

  /** Reverts a held-back tick on whichever people table shows this stakeholder (details dialog cancelled). */
  revertTick(person: KybPerson) {
    this.keyPersonChanges.delete(person.stakeholderId);
    const entity = this.reconciliationEntityForStakeholder(person.stakeholderId);
    if (entity) {
      this.syncReconciliationTouched(entity.commercialEntityId);
    }
    this.recomputeKeyPeopleValidation();
    this.resultBlocks?.forEach(block => block.revertTick(person));
  }

  applyConfirmedTick(
    person: KybPerson,
    isSelected: boolean,
    isGuarantor: boolean,
    isSigner: boolean,
  ): void {
    this.resultBlocks?.forEach(block => block.applyConfirmedTick(person, isSelected, isGuarantor, isSigner));
  }

  /**
   * This stakeholder as the people tables read them, for a caller that has only a stakeholderId and
   * needs to stage a tick the way a click on the table would - see onAddGuarantor on the KYC tab.
   */
  kybPersonFor(stakeholderId: number): KybPerson | null {
    for (const row of this.rows) {
      for (const entity of entityAndDescendants(row.entity)) {
        const person = kybEntityAsKeyPeopleResult(entity).people.find(
          candidate => candidate.stakeholderId === stakeholderId,
        );
        if (person) {
          return person;
        }
      }
    }
    return null;
  }

  onDownloadKybReport(entity: KybEntitySummary) {
    if (!entity.kybReportAvailable) {
      return;
    }
    const safeName = (entity.entityName || 'entity').replace(/[^\w.\-]+/g, '_');
    this.applicationService.downloadApplicationKybReportFn(
      this.applicationId,
      entity.commercialEntityId,
    ).pipe(
      this.portalHotToastService.loadingWithMessage('Downloading KYB report...'),
      this.portalHotToastService.publishErrorNotificationObservable({
        errorTitle: 'KYB report',
        errorMessage: 'Unable to download the KYB report',
        retryFn: () => this.onDownloadKybReport(entity),
      }),
      tap(blob => {
        openWindowAndDownloadWithFilename(`KYB-Report-${safeName}.pdf`, blob);
      }),
    ).subscribe();
  }

  isWorkflowStarting(entity: KybEntitySummary): boolean {
    return this.workflowStartingEntityIds.has(entity.commercialEntityId);
  }

  /**
   * Evaluate re-scores a stored result, so any entity with one qualifies, even while a newer run is
   * in progress or stuck. With no result yet there is nothing to re-score.
   */
  canReevaluate(entity: KybEntitySummary): boolean {
    return this.canReevaluateRisk && entity.hasKybResult && entity.resultKybRunId != null;
  }

  showEntityActions(entity: KybEntitySummary): boolean {
    return this.canEdit || this.canChangeParties() || this.isTrust(entity) || this.canReevaluate(entity);
  }

  /** Nested parties keep a menu for re-evaluation after documents freeze add and remove. */
  showNestedEntityActions(entity: KybEntitySummary): boolean {
    return this.canChangeParties() || this.canReevaluate(entity);
  }

  isReevaluationStarting(entity: KybEntitySummary): boolean {
    return this.reevaluationStartingEntityIds.has(entity.commercialEntityId);
  }

  onReevaluateRisk(entity: KybEntitySummary) {
    const runId = entity.resultKybRunId;
    if (
      !this.canReevaluate(entity)
      || runId == null
      || this.reevaluationStartingEntityIds.has(entity.commercialEntityId)
    ) {
      return;
    }
    this.reevaluationStartingEntityIds.add(entity.commercialEntityId);
    this.applicationService.retryKybReevaluationFn(runId).pipe(
      this.portalHotToastService.spinnerObservable(),
      tap(() => {
        this.applicationDialogService.openKybReevaluationStartedDialog();
      }),
      catchError(error => {
        this.portalHotToastService.error(
          error?.error?.message || 'Unable to send the risk re-evaluation',
          'Error',
        );
        return EMPTY;
      }),
      // No reload: nothing has changed yet, and a reload discards unsaved edits on this tab.
      finalize(() => {
        this.reevaluationStartingEntityIds.delete(entity.commercialEntityId);
      }),
    ).subscribe();
  }

  onRunKybWorkflow(entity: KybEntitySummary) {
    const entityId = entity.commercialEntityId;
    if (this.workflowStartingEntityIds.has(entityId)) {
      return;
    }
    this.workflowStartingEntityIds.add(entityId);
    this.applicationService.runApplicationKybWorkflowFn(this.applicationId, entityId).pipe(
      this.portalHotToastService.spinnerObservable(),
      tap(response => {
        this.portalHotToastService.success(
          response.message || 'A new KYB workflow has been started',
          'KYB workflow',
        );
      }),
      catchError(error => {
        this.portalHotToastService.error(
          error?.error?.message || 'Unable to start the KYB workflow',
          'KYB workflow',
        );
        return EMPTY;
      }),
      finalize(() => {
        this.workflowStartingEntityIds.delete(entityId);
        this.refresh();
      }),
    ).subscribe();
  }

  /** Every analysis on file for the entity, newest first; empty until a deed is uploaded. */
  trustAnalysesFor(entity: KybEntitySummary): TrustAnalysis[] {
    return this.trustAnalyses.get(entity.commercialEntityId) ?? [];
  }

  /** One block per deed condition; the version being viewed is chosen inside the block. */
  trustAnalysisHistoriesFor(entity: KybEntitySummary): TrustAnalysisHistory[] {
    return this.trustAnalysisHistories.get(entity.commercialEntityId) ?? [];
  }

  /**
   * The version the reviewer is looking at, defaulting to the newest.
   *
   * Held by run id rather than position so a poll or a newly uploaded deed reshuffling the list
   * leaves the reviewer on the deed they chose.
   */
  selectedTrustAnalysis(history: TrustAnalysisHistory): TrustAnalysis {
    const selectedRunId = this.selectedTrustRunIds.get(history.key);
    return history.versions.find(version => version.runId === selectedRunId) ?? history.versions[0];
  }

  onSelectTrustVersion(history: TrustAnalysisHistory, analysis: TrustAnalysis) {
    this.selectedTrustRunIds.set(history.key, analysis.runId);
  }

  trackTrustHistory(_index: number, history: TrustAnalysisHistory): string {
    return history.key;
  }

  /** Only a trust has a deed to analyse, so only a trust row offers the upload. */
  isTrust(entity: KybEntitySummary): boolean {
    return entity.entityType === 'trust';
  }

  /** Label the action by whether a deed is already on file, so a re-upload is not mistaken for the first. */
  trustDeedButtonLabel(entity: KybEntitySummary): string {
    return this.trustAnalysesFor(entity).length ? 'Replace Trust Deed' : 'Upload Trust Deed';
  }

  onUploadTrustDeed(entity: KybEntitySummary) {
    this.subscriptions.push(
      this.applicationDialogService.openUploadFileDialog({
        title: `Upload trust deed for ${entity.entityName}`,
      }).afterClosed().pipe(
        tap(async result => {
          if (!result?.valid || !result.files.length) {
            return;
          }
          const [file] = await filesToBase64Files(result.files);
          this.uploadTrustDeed(entity, file);
        }),
      ).subscribe(),
    );
  }

  private uploadTrustDeed(entity: KybEntitySummary, file: Base64File) {
    this.applicationService.uploadTrustDeedFn({
      applicationId: this.applicationId,
      commercialEntityId: entity.commercialEntityId,
      file,
      // Same condition the Documents tab row uses, so a deed uploaded here lands in that row
      // rather than in the generic legacy box.
      conditionValue: trustEntityDeedValue(entity.commercialEntityId),
    }).pipe(
      this.portalHotToastService.loadingWithMessage('Uploading trust deed...'),
      this.portalHotToastService.publishErrorNotificationObservable({
        errorTitle: 'Trust deed',
        errorMessage: 'Unable to upload the trust deed',
        retryFn: () => this.uploadTrustDeed(entity, file),
      }),
      this.portalHotToastService.snackBarObservable('Trust deed uploaded, analysis started'),
      tap(() => this.refreshTrustAnalyses()),
    ).subscribe();
  }

  /** Distinguishes one deed from another while the blocks are collapsed. */
  trustAnalysisSubtitle(analysis: TrustAnalysis): string {
    const uploaded = new Date(analysis.createdAt).toLocaleDateString();
    return `${analysis.fileName} — uploaded ${uploaded}`;
  }

  trackTrustAnalysis(_index: number, analysis: TrustAnalysis): number {
    return analysis.runId;
  }

  onRetryTrustAnalysis(analysis: TrustAnalysis) {
    this.applicationService.retryTrustAnalysisFn(this.applicationId, analysis.runId).pipe(
      this.portalHotToastService.loadingWithMessage('Re-running trust analysis...'),
      this.portalHotToastService.publishErrorNotificationObservable({
        errorTitle: 'Trust analysis',
        errorMessage: 'Unable to re-run the trust analysis',
        retryFn: () => this.onRetryTrustAnalysis(analysis),
      }),
      tap(() => this.refreshTrustAnalyses()),
    ).subscribe();
  }

  onDownloadTrustDeed(analysis: TrustAnalysis) {
    if (!analysis.blobPath) {
      return;
    }
    this.applicationService.downloadDocumentFromAzureFn(analysis.blobPath).pipe(
      this.portalHotToastService.loadingWithMessage('Downloading trust deed...'),
      this.portalHotToastService.publishErrorNotificationObservable({
        errorTitle: 'Trust deed',
        errorMessage: 'Unable to download the trust deed',
        retryFn: () => this.onDownloadTrustDeed(analysis),
      }),
      tap(payload => {
        const blob = new Blob([Buffer.from(payload.data as any)], { type: 'application/pdf' });
        openWindowAndDownloadWithFilename(analysis.fileName, blob);
      }),
    ).subscribe();
  }

  /** Cache every entity's current rule messages for its inline validation display. */
  private recomputeKeyPeopleValidation() {
    const changes = this.dirtyKeyPeople();
    this.keyPeopleValidation.clear();
    const issues: Array<{entityId: number; entityName: string; errors: string[]}> = [];
    for (const row of this.rows) {
      for (const entity of entityAndDescendants(row.entity)) {
        const result = kybEntityKeyPeopleValidation(entity, changes);
        this.keyPeopleValidation.set(entity.commercialEntityId, result);
        if (result.errors.length) {
          issues.push({
            entityId: entity.commercialEntityId,
            entityName: entity.entityName,
            errors: result.errors,
          });
        }
      }
    }
    this.keyPeopleRequirementIssueList = issues;
  }

  /** Entities whose signer/guarantor rules currently fail, including nested trustees/partners. */
  keyPeopleRequirementIssues(): ReadonlyArray<{
    entityId: number;
    entityName: string;
    errors: string[];
  }> {
    return this.keyPeopleRequirementIssueList;
  }

  rowHasKeyPeopleRuleErrors(entity: KybEntitySummary): boolean {
    return entityAndDescendants(entity).some(item => this.ruleErrorsFor(item).length > 0);
  }

  ruleErrorsFor(entity: KybEntitySummary): string[] {
    return this.keyPeopleValidation.get(entity.commercialEntityId)?.errors ?? NO_MESSAGES;
  }

  ruleWarningsFor(entity: KybEntitySummary): string[] {
    return this.keyPeopleValidation.get(entity.commercialEntityId)?.warnings ?? NO_MESSAGES;
  }

  /**
   * Entity ids whose persisted rules can change in this save. A guarantor flag belongs to the
   * application individual, so entities sharing that individual are affected even when only one
   * stakeholder row was edited.
   */
  private affectedKeyPeopleEntityIds(): Set<number> {
    const entities = this.rows.flatMap(row => entityAndDescendants(row.entity));
    const changedStakeholderIds = new Set(this.keyPersonChanges.keys());
    const changedIndividualIds = new Set<number>();

    for (const entity of entities) {
      for (const stakeholder of entity.stakeholders) {
        if (!changedStakeholderIds.has(stakeholder.stakeholderId)) {
          continue;
        }
        if (stakeholder.individualId != null) {
          changedIndividualIds.add(stakeholder.individualId);
        }
        const linkedRoleId = this.keyPersonChanges.get(stakeholder.stakeholderId)?.applicationIndividualRoleId;
        const linkedIndividualId = entity.reconciliation?.applicationPeople
          .find(person => person.applicationIndividualRoleId === linkedRoleId)?.individualId;
        if (linkedIndividualId != null) {
          changedIndividualIds.add(linkedIndividualId);
        }
      }
    }

    return new Set(
      entities
        .filter(entity => entity.stakeholders.some(stakeholder =>
          changedStakeholderIds.has(stakeholder.stakeholderId)
          || (stakeholder.individualId != null && changedIndividualIds.has(stakeholder.individualId))))
        .map(entity => entity.commercialEntityId),
    );
  }

  private keyPeopleSaveRuleErrors(): string[] {
    const affectedEntityIds = this.affectedKeyPeopleEntityIds();
    const messages = new Set<string>();
    for (const [entityId, result] of this.keyPeopleValidation) {
      if (!affectedEntityIds.has(entityId)) {
        continue;
      }
      for (const error of result.errors) {
        messages.add(error);
      }
    }
    return [...messages];
  }

  /**
   * Whether the dirty ticks break a rule for an affected entity, so an unrelated invalid entity does
   * not prevent the tab-level Save.
   *
   * Only gates while there are key-people edits to save: a violation already sitting in the data -
   * a company whose sole director was never made a guarantor, say - must not block an unrelated
   * individual edit, and the server likewise only checks the rules when key people change.
   */
  hasKeyPeopleRuleErrors(): boolean {
    return this.keyPersonChanges.size > 0
      && this.keyPeopleSaveRuleErrors().length > 0;
  }

  /** The rule violations currently blocking a save, for the tab-level Save click to report. */
  keyPeopleRuleErrorMessages(): string[] {
    return this.keyPeopleSaveRuleErrors();
  }

  /** Read by the tab-level Save, alongside dirtyEntities, so everything goes out in one request. */
  dirtyKeyPeople(): KybPersonRowState[] {
    return [...this.keyPersonChanges.values()];
  }

  /** Stable parent-owned state used to restore ticks after an expanded entity is destroyed. */
  stagedKeyPeople(): ReadonlyMap<number, KybPersonRowState> {
    return this.keyPersonChanges;
  }

  dirtyReconciliations(): KybReconciliationSaveData[] {
    return this.rows
      .flatMap(row => entityAndDescendants(row.entity))
      .flatMap(entity => {
        const change = this.reconciliationChanges.get(entity.commercialEntityId);
        return change ? [change] : [];
      });
  }

  // Links and additions are also staged on entities with no people review, to label their rows; those
  // are saved as key-person ticks and are not reconciliation work.
  hasStagedReconciliationWork(): boolean {
    return this.rows
      .flatMap(row => entityAndDescendants(row.entity))
      .some(entity =>
        this.requiresReconciliation(entity) && this.touchedReconciliationEntityIds.has(entity.commercialEntityId));
  }

  stakeholderRequiresReconciliation(stakeholderId: number): boolean {
    const entity = this.reconciliationEntityForStakeholder(stakeholderId);
    return !!entity && this.requiresReconciliation(entity);
  }

  reconciliationCandidates(stakeholderId: number): {
    applicationIndividualRoleId: number | null;
    individualId: number;
    name: string;
    dateOfBirth: string | null;
    suggested: boolean;
  }[] {
    const entity = this.reconciliationEntityForStakeholder(stakeholderId);
    if (entity?.reconciliation?.status !== 'REVIEW_REQUIRED') return [];
    const stakeholder = entity.stakeholders.find(candidate => candidate.stakeholderId === stakeholderId);
    if (!stakeholder) return [];
    const takenIndividualIds = this.takenApplicationIndividualIds(entity, stakeholderId);
    const applicationOnlyRoleIds = new Set(
      entity.reconciliation.comparisons.flatMap(comparison =>
        comparison.applicationIndividualRoleId == null ? [] : [comparison.applicationIndividualRoleId]),
    );
    return entity.reconciliation.applicationPeople.flatMap(person =>
      !takenIndividualIds.has(person.individualId)
      && (
        person.applicationIndividualRoleId == null
        || applicationOnlyRoleIds.has(person.applicationIndividualRoleId)
      )
      // A suggestion for staff to confirm, so wider than automatic linking: any two of name, surname and
      // date of birth, or any pair the shared rule sees as one name (a middle name in the first-name
      // field, say), even with different dates of birth.
      && (personIdentityPartialMatch(stakeholder, person) || compareIdentity(stakeholder, person) !== 'none')
        ? [{
          applicationIndividualRoleId: person.applicationIndividualRoleId,
          individualId: person.individualId,
          name: `${person.givenName} ${person.surname}`.trim() || 'Individual',
          dateOfBirth: person.dateOfBirth || null,
          suggested: false,
        }]
        : [],
    );
  }

  hasStagedKybResolution(stakeholderId: number): boolean {
    const entity = this.reconciliationEntityForStakeholder(stakeholderId);
    if (!entity) return false;
    const entityId = entity.commercialEntityId;
    return this.reconciliationLinks.get(entityId)?.has(stakeholderId) === true
      || this.reconciliationAdditions.get(entityId)?.has(stakeholderId) === true;
  }

  stagedResolutions(entity: KybEntitySummary): ReadonlyMap<number, KybPersonResolution> {
    const resolutions = new Map<number, KybPersonResolution>();
    const entityId = entity.commercialEntityId;
    const links = this.reconciliationLinks.get(entityId);
    if (links) {
      for (const [stakeholderId, individualId] of links) {
        const person = entity.reconciliation?.applicationPeople.find(
          candidate => candidate.individualId === individualId,
        );
        resolutions.set(stakeholderId, {
          kind: 'existing',
          name: `${person?.givenName ?? ''} ${person?.surname ?? ''}`.trim()
            || this.reconciliationLinkNames.get(stakeholderId)
            || 'Individual',
        });
      }
    }
    const additions = this.reconciliationAdditions.get(entityId);
    if (additions) {
      for (const [stakeholderId, person] of additions) {
        resolutions.set(stakeholderId, {
          kind: 'new',
          name: this.manualPersonDisplayName(person),
        });
      }
    }
    return resolutions;
  }

  private manualPersonDisplayName(person: KybManualPerson | undefined): string {
    const name = `${person?.givenName ?? ''} ${person?.surname ?? ''}`.trim();
    return name || 'Individual';
  }

  liveKybPerson(stakeholderId: number): KybPerson | null {
    for (const block of this.resultBlocks ?? []) {
      const person = block.livePerson(stakeholderId);
      if (person) return person;
    }
    return null;
  }

  /** Whether another stakeholder of the same entity is already staged as linked to this individual. */
  isApplicationIndividualLinkedElsewhere(stakeholderId: number, applicationIndividualId: number): boolean {
    const entity = this.reconciliationEntityForStakeholder(stakeholderId);
    return !!entity && this.takenApplicationIndividualIds(entity, stakeholderId).has(applicationIndividualId);
  }

  /**
   * One application person can only match one KYB stakeholder. Returns any stakeholder whose
   * previous link was displaced so the caller can untick that row.
   */
  stageKybPersonLink(stakeholderId: number, applicationIndividualId: number, displayName?: string): number[] {
    const entity = this.reconciliationEntityForStakeholder(stakeholderId);
    if (!entity) return [];
    const entityId = entity.commercialEntityId;
    const links = this.reconciliationLinks.get(entityId) ?? new Map<number, number>();
    const displaced = [...links.entries()]
      .filter(([linkedStakeholderId, linkedIndividualId]) =>
        linkedIndividualId === applicationIndividualId && linkedStakeholderId !== stakeholderId)
      .map(([linkedStakeholderId]) => linkedStakeholderId);
    for (const linkedStakeholderId of displaced) {
      links.delete(linkedStakeholderId);
      this.reconciliationLinkNames.delete(linkedStakeholderId);
    }
    links.set(stakeholderId, applicationIndividualId);
    if (displayName) {
      this.reconciliationLinkNames.set(stakeholderId, displayName);
    } else {
      this.reconciliationLinkNames.delete(stakeholderId);
    }
    this.reconciliationLinks.set(entityId, links);
    this.touchedReconciliationEntityIds.add(entityId);
    this.reconciliationAdditions.get(entityId)?.delete(stakeholderId);
    const linkedRoleId = entity.reconciliation?.applicationPeople.find(
      person => person.individualId === applicationIndividualId,
    )?.applicationIndividualRoleId;
    if (linkedRoleId != null) {
      this.applicationReconciliationDecisions.get(entityId)?.delete(linkedRoleId);
    }
    this.reconciliationChanges.delete(entityId);
    this.reconciliationValidation.delete(entityId);
    return displaced;
  }

  stageKybPersonAddition(stakeholderId: number, person: KybManualPerson): void {
    const entity = this.reconciliationEntityForStakeholder(stakeholderId);
    if (!entity) return;
    const entityId = entity.commercialEntityId;
    const additions = this.reconciliationAdditions.get(entityId) ?? new Map<number, KybManualPerson>();
    const state = this.keyPersonChanges.get(stakeholderId);
    additions.set(stakeholderId, {
      ...person,
      commercialEntityId: entityId,
      ...(state ? {isGuarantor: state.isGuarantor, isSigner: state.isSigner} : {}),
    });
    this.reconciliationAdditions.set(entityId, additions);
    this.touchedReconciliationEntityIds.add(entityId);
    this.reconciliationLinks.get(entityId)?.delete(stakeholderId);
    this.reconciliationLinkNames.delete(stakeholderId);
    this.reconciliationChanges.delete(entityId);
    this.reconciliationValidation.delete(entityId);
  }

  clearKybPersonResolution(stakeholderId: number): void {
    const entity = this.reconciliationEntityForStakeholder(stakeholderId);
    if (!entity) return;
    const entityId = entity.commercialEntityId;
    this.reconciliationLinks.get(entityId)?.delete(stakeholderId);
    this.reconciliationLinkNames.delete(stakeholderId);
    this.reconciliationAdditions.get(entityId)?.delete(stakeholderId);
    this.reconciliationChanges.delete(entityId);
    this.reconciliationValidation.delete(entityId);
    this.syncReconciliationTouched(entityId);
  }

  unmatchedApplicationPeople(entity: KybEntitySummary): KybReconciliationApplicationPerson[] {
    const reconciliation = entity.reconciliation;
    if (reconciliation?.status !== 'REVIEW_REQUIRED') return [];
    const linkedIndividualIds = new Set(this.reconciliationLinks.get(entity.commercialEntityId)?.values());
    const applicationOnlyRoleIds = new Set(
      reconciliation.comparisons
        .filter(item => item.applicationIndividualRoleId != null && item.applicationKybStakeholderId == null)
        .map(item => item.applicationIndividualRoleId as number),
    );
    return reconciliation.applicationPeople.filter(person =>
      person.applicationIndividualRoleId != null
      && applicationOnlyRoleIds.has(person.applicationIndividualRoleId)
      && !linkedIndividualIds.has(person.individualId),
    );
  }

  applicationReconciliationReviewsFor(
    individualLegacyId: string | null | undefined,
  ): KybApplicationReconciliationReview[] {
    if (!individualLegacyId) return [];
    const reviews: KybApplicationReconciliationReview[] = [];
    for (const row of this.rows) {
      for (const entity of entityAndDescendants(row.entity)) {
        for (const person of this.unmatchedApplicationPeople(entity)) {
          if (person.clientId !== individualLegacyId) continue;
          reviews.push({
            entity,
            person,
            decision: this.applicationReconciliationDecision(entity, person),
            error: this.reconciliationValidation.get(entity.commercialEntityId) ?? null,
          });
        }
      }
    }
    return reviews;
  }

  applicationReconciliationDecision(
    entity: KybEntitySummary,
    person: KybReconciliationApplicationPerson,
  ): 'KEEP' | 'REMOVE' | null {
    if (person.applicationIndividualRoleId == null) return null;
    return this.applicationReconciliationDecisions
      .get(entity.commercialEntityId)
      ?.get(person.applicationIndividualRoleId)
      ?? null;
  }

  setApplicationReconciliationDecision(
    entity: KybEntitySummary,
    person: KybReconciliationApplicationPerson,
    decision: 'KEEP' | 'REMOVE',
  ): void {
    if (person.applicationIndividualRoleId == null) return;
    const decisions = this.applicationReconciliationDecisions.get(entity.commercialEntityId)
      ?? new Map<number, 'KEEP' | 'REMOVE'>();
    decisions.set(person.applicationIndividualRoleId, decision);
    this.applicationReconciliationDecisions.set(entity.commercialEntityId, decisions);
    this.touchedReconciliationEntityIds.add(entity.commercialEntityId);
    this.reconciliationChanges.delete(entity.commercialEntityId);
    this.reconciliationValidation.delete(entity.commercialEntityId);
  }

  confirmPeopleReview(entity: KybEntitySummary): void {
    const reconciliation = entity.reconciliation;
    if (reconciliation?.status !== 'REVIEW_REQUIRED') return;
    const entityId = entity.commercialEntityId;
    const links = this.reconciliationLinks.get(entityId) ?? new Map<number, number>();
    const additions = this.reconciliationAdditions.get(entityId) ?? new Map<number, KybManualPerson>();
    const applicationDecisions = this.applicationReconciliationDecisions.get(entityId)
      ?? new Map<number, 'KEEP' | 'REMOVE'>();
    const linkedIndividualIds = new Set(links.values());
    const actions: KybReconciliationActionInput[] = [];

    for (const comparison of reconciliation.comparisons) {
      const roleId = comparison.applicationIndividualRoleId;
      const stakeholderId = comparison.applicationKybStakeholderId;
      if (roleId != null) {
        const person = reconciliation.applicationPeople.find(
          candidate => candidate.applicationIndividualRoleId === roleId,
        );
        if (person && linkedIndividualIds.has(person.individualId)) {
          continue;
        }
        const action = applicationDecisions.get(roleId);
        if (!action) {
          this.reconciliationValidation.set(
            entityId,
            'Choose Keep or Remove for every existing person not returned by KYB.',
          );
          return;
        }
        actions.push({
          action,
          applicationIndividualRoleId: roleId,
        });
      } else if (stakeholderId != null) {
        const linkedIndividualId = links.get(stakeholderId);
        if (linkedIndividualId != null) {
          const linkedPerson = reconciliation.applicationPeople.find(
            person => person.individualId === linkedIndividualId,
          );
          actions.push({
            action: 'LINK',
            applicationIndividualId: linkedIndividualId,
            applicationIndividualRoleId: linkedPerson?.applicationIndividualRoleId ?? null,
            applicationKybStakeholderId: stakeholderId,
          });
          continue;
        }
        const newPerson = additions.get(stakeholderId);
        if (newPerson) {
          actions.push({
            action: 'ADD',
            applicationKybStakeholderId: stakeholderId,
            newPerson,
          });
          continue;
        }
        const person = kybEntityAsKeyPeopleResult(entity).people.find(
          candidate => candidate.stakeholderId === stakeholderId,
        );
        const changed = this.keyPersonChanges.get(stakeholderId);
        const selected = changed?.isSelected
          ?? (person ? kybResolveSelected(entity.entityType, person) : false);
        if (selected && person) {
          this.reconciliationValidation.set(
            entityId,
            'Choose an existing application person or add the selected KYB person.',
          );
          return;
        }
        actions.push({
          action: 'IGNORE',
          applicationKybStakeholderId: stakeholderId,
        });
      } else {
        this.reconciliationValidation.set(entityId, 'Every person must be resolved before confirmation.');
        return;
      }
    }

    this.reconciliationValidation.delete(entityId);
    this.reconciliationChanges.set(entityId, {
      reconciliationGuid: reconciliation.reconciliationGuid,
      expectedFingerprint: reconciliation.fingerprint,
      reviewNote: this.reconciliationReviewNotes.get(entityId)?.trim() || null,
      actions,
    });
  }

  prepareReconciliationForSave(): void {
    for (const entityId of this.touchedReconciliationEntityIds) {
      this.reconciliationValidation.delete(entityId);
      this.reconciliationChanges.delete(entityId);
    }
    for (const row of this.rows) {
      for (const entity of entityAndDescendants(row.entity)) {
        if (
          entity.reconciliation?.status === 'REVIEW_REQUIRED'
          && this.touchedReconciliationEntityIds.has(entity.commercialEntityId)
        ) {
          this.confirmPeopleReview(entity);
        }
      }
    }
  }

  reconciliationIncompleteMessage(): string | null {
    return [...this.reconciliationValidation.values()][0] ?? null;
  }

  private syncReconciliationTouched(entityId: number): void {
    const touched = !!this.reconciliationLinks.get(entityId)?.size
      || !!this.reconciliationAdditions.get(entityId)?.size
      || !!this.applicationReconciliationDecisions.get(entityId)?.size
      || !!this.reconciliationReviewNotes.get(entityId)?.trim();
    if (touched) {
      this.touchedReconciliationEntityIds.add(entityId);
    } else {
      this.touchedReconciliationEntityIds.delete(entityId);
      this.reconciliationChanges.delete(entityId);
      this.reconciliationValidation.delete(entityId);
    }
  }

  private restoreReconciliationDrafts(drafts: KybReconciliationDraftRestore[]): void {
    for (const draft of drafts) {
      const entity = this.rows
        .flatMap(row => entityAndDescendants(row.entity))
        .find(candidate =>
          candidate.reconciliation?.reconciliationGuid === draft.data.reconciliationGuid);
      if (!entity || entity.reconciliation?.status !== 'REVIEW_REQUIRED') continue;
      const entityId = entity.commercialEntityId;
      this.touchedReconciliationEntityIds.add(entityId);
      if (draft.error) {
        this.reconciliationValidation.set(entityId, draft.error);
      }
      if (draft.data.expectedFingerprint !== entity.reconciliation.fingerprint) {
        continue;
      }
      if (draft.data.reviewNote?.trim()) {
        this.reconciliationReviewNotes.set(entityId, draft.data.reviewNote);
      }
      this.reconciliationChanges.set(entityId, draft.data);
      const validRoleIds = new Set(
        entity.reconciliation.comparisons.flatMap(comparison =>
          comparison.applicationIndividualRoleId == null ? [] : [comparison.applicationIndividualRoleId]),
      );
      const validStakeholderIds = new Set(
        entity.reconciliation.comparisons.flatMap(comparison =>
          comparison.applicationKybStakeholderId == null ? [] : [comparison.applicationKybStakeholderId]),
      );
      const validIndividualIds = new Set(
        entity.reconciliation.applicationPeople.map(person => person.individualId),
      );
      for (const action of draft.data.actions) {
        if (
          action.action === 'LINK'
          && action.applicationKybStakeholderId != null
          && action.applicationIndividualId != null
          && validStakeholderIds.has(action.applicationKybStakeholderId)
          && validIndividualIds.has(action.applicationIndividualId)
        ) {
          const links = this.reconciliationLinks.get(entityId) ?? new Map<number, number>();
          links.set(action.applicationKybStakeholderId, action.applicationIndividualId);
          this.reconciliationLinks.set(entityId, links);
          const person = entity.stakeholders.find(
            stakeholder => stakeholder.stakeholderId === action.applicationKybStakeholderId,
          );
          this.keyPersonChanges.set(action.applicationKybStakeholderId, {
            stakeholderId: action.applicationKybStakeholderId,
            isSelected: true,
            isGuarantor: person?.isGuarantor ?? false,
            isSigner: person?.isSigner ?? false,
            applicationIndividualId: action.applicationIndividualId,
            ...(action.applicationIndividualRoleId != null
              ? {applicationIndividualRoleId: action.applicationIndividualRoleId}
              : {}),
          });
        } else if (
          action.action === 'ADD'
          && action.applicationKybStakeholderId != null
          && validStakeholderIds.has(action.applicationKybStakeholderId)
          && action.newPerson
        ) {
          const additions = this.reconciliationAdditions.get(entityId) ?? new Map<number, KybManualPerson>();
          additions.set(action.applicationKybStakeholderId, action.newPerson);
          this.reconciliationAdditions.set(entityId, additions);
          this.keyPersonChanges.set(action.applicationKybStakeholderId, {
            stakeholderId: action.applicationKybStakeholderId,
            isSelected: true,
            isGuarantor: action.newPerson.isGuarantor,
            isSigner: action.newPerson.isSigner,
          });
        } else if (
          action.action === 'IGNORE'
          && action.applicationKybStakeholderId != null
          && validStakeholderIds.has(action.applicationKybStakeholderId)
        ) {
          const person = entity.stakeholders.find(
            stakeholder => stakeholder.stakeholderId === action.applicationKybStakeholderId,
          );
          this.keyPersonChanges.set(action.applicationKybStakeholderId, {
            stakeholderId: action.applicationKybStakeholderId,
            isSelected: false,
            isGuarantor: person?.isGuarantor ?? false,
            isSigner: person?.isSigner ?? false,
          });
        } else if (
          (action.action === 'KEEP' || action.action === 'REMOVE')
          && action.applicationIndividualRoleId != null
          && validRoleIds.has(action.applicationIndividualRoleId)
        ) {
          const applicationDecisions = this.applicationReconciliationDecisions.get(entityId)
            ?? new Map<number, Extract<KybReconciliationAction, 'KEEP' | 'REMOVE'>>();
          applicationDecisions.set(action.applicationIndividualRoleId, action.action);
          this.applicationReconciliationDecisions.set(entityId, applicationDecisions);
        }
      }
    }
  }

  private takenApplicationIndividualIds(entity: KybEntitySummary, stakeholderId: number): Set<number> {
    const taken = new Set<number>();
    const links = this.reconciliationLinks.get(entity.commercialEntityId);
    if (links) {
      for (const [linkedStakeholderId, individualId] of links) {
        if (linkedStakeholderId !== stakeholderId) taken.add(individualId);
      }
    }
    return taken;
  }

  private reconciliationEntityForStakeholder(stakeholderId: number): KybEntitySummary | null {
    return this.rows
      .flatMap(row => entityAndDescendants(row.entity))
      .find(entity => entity.stakeholders.some(person => person.stakeholderId === stakeholderId))
      ?? null;
  }

  /**
   * Who this save adds to or removes from the application, for the save confirmation dialog. Only
   * these have a consent consequence: a new party is issued a form and a removed one has an unsigned
   * form voided, while a guarantor/signer tick on someone who is a party either way changes nothing
   * they have consented to.
   *
   * Compared against the loaded stakeholders rather than the collected ticks alone, because ticking a
   * box and unticking it again leaves a collected row that changes nothing and must not be announced.
   */
  partyChanges(): { stakeholderId: number; name: string; becameParty: boolean }[] {
    const changes: { stakeholderId: number; name: string; becameParty: boolean }[] = [];
    for (const row of this.rows) {
      for (const entity of entityAndDescendants(row.entity)) {
        const resolutions = this.stagedResolutions(entity);
        for (const stakeholder of entity.stakeholders) {
          const change = this.keyPersonChanges.get(stakeholder.stakeholderId);
          if (!change || change.isSelected === kybResolveSelected(entity.entityType, stakeholder)) {
            continue;
          }
          changes.push({
            stakeholderId: stakeholder.stakeholderId,
            name: resolutions.get(stakeholder.stakeholderId)?.name
              ?? (`${stakeholder.givenName} ${stakeholder.surname}`.trim() || 'Individual'),
            becameParty: change.isSelected,
          });
        }
      }
    }
    return changes;
  }

  entityForm(row: KybEntityRow): FormGroup<KybKeyCompanyDetailsForm> | null {
    return this.entityForms.get(row.entity.commercialEntityId) ?? null;
  }

  /**
   * Read by the tab-level Save so entity and individual edits go out in one request.
   *
   * Compared against the values as loaded rather than trusting FormGroup.dirty: the annual revenue
   * and address controls emit through their value accessors as they initialise, which marks the group
   * dirty before the reviewer has touched anything. A dirty entity form announces - and saves - a
   * consent reissue for every signatory, so it has to mean an actual edit.
   */
  dirtyEntities(): SaveApplicationPartyEntityInput[] {
    const dirty: SaveApplicationPartyEntityInput[] = [];
    for (const [commercialEntityId, group] of this.entityForms) {
      if (group.disabled || !this.entityValuesChanged(commercialEntityId, group)) {
        continue;
      }
      const value = group.getRawValue();
      const loaded = this.pristineEntityValues.get(commercialEntityId);
      dirty.push({
        commercialEntityId,
        data: {
          // Revenue and address fall back to what was loaded: a null from either control means it could
          // not represent the stored value, not that the reviewer cleared the field, so sending the
          // null would wipe a value nobody touched.
          annualRevenue: value.annualRevenue ?? loaded?.annualRevenue ?? null,
          // One Business Phone field is captured, so the whole value is stored as the number and the
          // area code cleared; leaving a stale areaCode would prefix it on the consent form.
          areaCode: null,
          phoneNumber: (value.businessPhone ?? '').trim() || null,
          // Sent only when actually edited: the API reads an absent email as "untouched", so a
          // stored value that predates email validation cannot fail an unrelated edit to the block.
          businessContactEmail: kybCompanyDetailsEmailChanged(loaded, value)
            ? (value.businessContactEmail ?? '').trim() || null
            : undefined,
          principalPlaceOfBusinessAddress: value.principalPlaceOfBusinessAddress
            ?? loaded?.principalPlaceOfBusinessAddress
            ?? null,
        },
      });
    }
    return dirty;
  }

  hasInvalidCompanyDetails(): boolean {
    return [...this.entityForms.entries()].some(([commercialEntityId, group]) => {
      if (group.disabled || !group.invalid || !this.entityValuesChanged(commercialEntityId, group)) {
        return false;
      }
      // The email is the only validated field, and an unchanged email is left out of the payload -
      // so a value already invalid as loaded (saved before validation existed) must not hold back
      // saving the fields the reviewer actually edited.
      return kybCompanyDetailsEmailChanged(this.pristineEntityValues.get(commercialEntityId), group.getRawValue());
    });
  }

  // The company-block fields the reviewer edited on this entity, with old/new values for the
  // audit rows written by the tab-level Save. Empty when the entity is not dirty.
  dirtyEntityChangedEntries(commercialEntityId: number): SavePartyChangedField[] {
    const group = this.entityForms.get(commercialEntityId);
    const pristine = this.pristineEntityValues.get(commercialEntityId);
    if (!group || group.disabled || !pristine) {
      return [];
    }
    return kybCompanyDetailsChangedEntries(pristine, group.getRawValue());
  }

  private entityValuesChanged(commercialEntityId: number, group: FormGroup<KybKeyCompanyDetailsForm>): boolean {
    const pristine = this.pristineEntityValues.get(commercialEntityId);
    return !!pristine && kybCompanyDetailsChanged(pristine, group.getRawValue());
  }

  isTableRowExpanded(row: KybEntityRow): boolean {
    return this.expandedEntityId === row.entity.commercialEntityId;
  }

  expandTableRow(row: KybEntityRow) {
    this.expandedEntityId = this.isTableRowExpanded(row) ? null : row.entity.commercialEntityId;
  }

  /** Corporate trustee/partner detail blocks start collapsed; the header bar carries the summary. */
  private expandedChildIds = new Set<number>();

  isChildExpanded(child: KybEntitySummary): boolean {
    return this.expandedChildIds.has(child.commercialEntityId);
  }

  toggleChild(child: KybEntitySummary) {
    const id = child.commercialEntityId;
    if (this.expandedChildIds.has(id)) {
      this.expandedChildIds.delete(id);
    } else {
      this.expandedChildIds.add(id);
    }
  }

  /** Add/remove controls only for internal editors before the party structure is locked. */
  canChangeParties(): boolean {
    return this.canEdit && !this.partyStructureLocked;
  }

  /**
   * The party kind an entity accepts: a trust takes trustees, a partnership takes partners, anything
   * else takes none (the control is hidden). Drives both the button label and the add dialog.
   */
  addablePartyKind(entity: KybEntitySummary): 'trustee' | 'partner' | null {
    if (entity.entityType === 'trust') {
      return 'trustee';
    }
    if (entity.entityType === 'partnership') {
      return 'partner';
    }
    return null;
  }

  /** Button label for the per-entity add, e.g. "Add trustee" / "Add partner". */
  addPartyLabel(entity: KybEntitySummary): string {
    const kind = this.addablePartyKind(entity);
    return kind === 'trustee' ? 'Add trustee' : 'Add partner';
  }

  onAddParty(entity: KybEntitySummary) {
    this.addParty.emit(entity);
  }

  /** Add a natural-person trustee/partner under this entity. */
  onAddIndividualParty(entity: KybEntitySummary) {
    this.addIndividualParty.emit(entity);
  }

  onRemoveParty(entity: KybEntitySummary) {
    this.removeParty.emit(entity);
  }

  onUndoRemoveParty(entity: KybEntitySummary) {
    this.undoRemoveParty.emit(entity);
  }

  /** Stage (or unstage) an entity for removal; staged rows render struck-through until Save. */
  stageEntityRemoval(entity: KybEntitySummary) {
    this.stagedEntityRemovals.set(entity.commercialEntityId, entity);
  }

  unstageEntityRemoval(commercialEntityId: number) {
    this.stagedEntityRemovals.delete(commercialEntityId);
  }

  isRemovalStaged(entity: KybEntitySummary): boolean {
    return this.stagedEntityRemovals.has(entity.commercialEntityId);
  }

  /** Read by the tab-level Save so removals go out in the same request as edits and ticks. */
  stagedRemovals(): RemoveApplicationPartyInput[] {
    const entities: RemoveApplicationPartyInput[] = [...this.stagedEntityRemovals.keys()]
      .map(id => ({ kind: 'entity' as const, id }));
    const individuals: RemoveApplicationPartyInput[] = [...this.stagedIndividualRemovals.values()]
      .map(p => ({
        kind: 'individual' as const,
        id: p.individualId,
        role: p.isTrustee ? ('trustee' as const) : ('partner' as const),
      }));
    return [...entities, ...individuals];
  }

  /** Names of staged removals, for the save confirmation dialog. */
  stagedRemovalNames(): string[] {
    return [...this.stagedEntityRemovals.values()].map(e => e.entityName);
  }

  /** Relay an individual trustee/partner removal from a result block up to the tab. */
  onRemoveIndividualParty(party: KybEntityIndividualPartySummary) {
    this.removeIndividualParty.emit(party);
  }

  /** Stage (or unstage) an individual trustee/partner for removal; applied on the tab-level Save. */
  stageIndividualRemoval(party: KybEntityIndividualPartySummary) {
    this.stagedIndividualRemovals.set(party.individualId, party);
  }

  unstageIndividualRemoval(individualId: number) {
    this.stagedIndividualRemovals.delete(individualId);
  }

  isIndividualRemovalStaged(individualId: number): boolean {
    return this.stagedIndividualRemovals.has(individualId);
  }

  /** Unstage an individual removal from its party-list row. */
  onUndoRemoveIndividualParty(party: KybEntityIndividualPartySummary) {
    this.unstageIndividualRemoval(party.individualId);
  }

  /** Names of staged individual removals, for the save confirmation dialog. */
  stagedIndividualRemovalNames(): string[] {
    return [...this.stagedIndividualRemovals.values()].map(p => kybEntityIndividualPartyName(p));
  }

  /**
   * Drives the alert icon on a collapsed child's header bar, so a trustee/partner with a failed
   * check, reported issues, or unmet signer rules is not invisible while its detail is collapsed.
   * Reads the whole chain beneath the child, because collapsing it takes its own corporate parties
   * off screen with it.
   */
  childNeedsAttention(child: KybEntitySummary): boolean {
    return entityAndDescendants(child).some(entity =>
      entity.issueCount > 0
      || entity.reconciliation?.status === 'REVIEW_REQUIRED'
      || kybEntityBlocklistCheck(entity).passed === false
      || kybEntityAmlScreeningCheck(entity).passed === false
      || this.ruleErrorsFor(entity).length > 0);
  }

  reconciliationAttentionCount(): number {
    return this.rows
      .flatMap(row => entityAndDescendants(row.entity))
      .filter(entity => this.requiresReconciliation(entity))
      .length;
  }

  requiresReconciliation(entity: KybEntitySummary): boolean {
    return entity.reconciliation?.status === 'REVIEW_REQUIRED';
  }

  rowRequiresReconciliation(entity: KybEntitySummary): boolean {
    return entityAndDescendants(entity).some(item => this.requiresReconciliation(item));
  }
}

/** Shared empty array, so an entity with no rule messages does not rebind a new one each cycle. */
const NO_MESSAGES: string[] = [];

/**
 * The entity with every corporate trustee/partner beneath it, at any depth. The chain is not capped
 * at one level: a trust can be held by a partnership trustee that itself has a company partner, and
 * that company is a party to the application like any other.
 */
const entityAndDescendants = (entity: KybEntitySummary): KybEntitySummary[] =>
  [entity, ...entity.children.flatMap(entityAndDescendants)];

/** The entity's own stakeholders plus those of the corporate trustees/partners shown inside its row. */
const stakeholderIdsOf = (entity: KybEntitySummary): number[] =>
  entityAndDescendants(entity).flatMap(e => e.stakeholders.map(stakeholder => stakeholder.stakeholderId));

const joinBusinessPhone = (details: KybEntityKeyCompanyDetails): string | null =>
  [details.areaCode, details.phoneNumber].filter(part => !!part).join(' ').trim() || null;
